import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import Paper from '@mui/material/Paper';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  FormControlLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Switch,
  Select,
  Stack,
  TextField,
  Typography,
  styled,
  createTheme,
  useMediaQuery,
} from '@mui/material';
import './style.css';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enIN } from 'date-fns/locale';
import LeadStore from '../../store/LeadStore';
import dayjs from 'dayjs';
import { isEmpty } from '../../utils/helper';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const CustomFormControlLabel = styled(FormControlLabel)({
  '& .MuiFormControlLabel-label': {
    fontSize: '0.75rem',
    color: '#0009',
  },
});

export const EditLeadPcpDetails = observer(({ leadId, details, handleEditPcpDetails }) => {
  const theme = createTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    id: details?.clientPackageAndPaymentId ?? null,
    leadId,
    // Signed-up Packages
    toggleResidentialPackage: !isEmpty(details?.residentialPackage),
    residentialPackage: details?.residentialPackage,
    residentialPackageOther: details?.residentialPackageOther,
    toggleDesignsPackage: !isEmpty(details?.designsPackage),
    designsPackage: details?.designsPackage,
    toggleCommercialPackage: !isEmpty(details?.commercialPackage),
    commercialPackage: details?.commercialPackage,
    commercialPackageOther: details?.commercialPackageOther,
    toggleInteriorsPackage: !isEmpty(details?.interiorsPackage),
    interiorsPackage: details?.interiorsPackage,
    interiorsPackageOther: details?.interiorsPackageOther,
    // Construction Details
    projectCode: details?.projectCode,
    tentativeProjectValue: details?.tentativeProjectValue,
    finalProjectValue: details?.finalProjectValue,
    constructionValue: details?.constructionValue,
    elevationValue: details?.elevationValue,
    additionalChargedInTotal: details?.additionalChargedInTotal,
    signUpDate: details?.signUpDate,
    soilTestDate: details?.soilTestDate,
    totalStationDate: details?.totalStationDate,
    floorPlanFinalisedDate: details?.floorPlanFinalisedDate,
    elevationFinalisedDate: details?.elevationFinalisedDate,
    masterFileFinalisedDate: details?.masterFileFinalisedDate,
    tentativeCommencementDate: details?.tentativeCommencementDate,
    // Team
    projectManager: details?.projectManager,
    siteEngineer: details?.siteEngineer,
    architect: details?.architect,
    salesPartner: details?.salesPartner,
    // Payment Details
    paymentMadeBy: details?.paymentMadeBy,
    receivedBy: details?.receivedBy,
    billingName: details?.billingName,
    billingDate: details?.billingDate,
    paymentSignUpDate: details?.paymentSignUpDate,
    contractDate: details?.contractDate,
    paymentMode: details?.paymentMode,
    paymentModeOther: details?.paymentModeOther,
    paymentSignUpAmount: details?.paymentSignUpAmount,
    contractAmount: details?.contractAmount,
    listOfAdditionalsPromised: details?.listOfAdditionalsPromised,
    // Other
    createdBy: details?.CreatedBy,
    createdDate: details?.createdDate,
    updatedBy: details?.updatedBy,
    updatedDate: details?.updatedDate,
  });

  const [errors, setErrors] = useState({
    residentialPackage: false,
    residentialPackageOther: false,
    designsPackage: false,
    commercialPackage: false,
    commercialPackageOther: false,
    interiorsPackage: false,
    interiorsPackageOther: false,
    paymentMadeBy: false,
    receivedBy: false,
    billingName: false,
    billingDate: false,
    paymentSignUpDate: false,
    contractDate: false,
    paymentMode: false,
    paymentModeOther: false,
    paymentSignUpAmount: false,
    contractAmount: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    residentialPackage: '',
    residentialPackageOther: '',
    designsPackage: '',
    commercialPackage: '',
    commercialPackageOther: '',
    interiorsPackage: '',
    interiorsPackageOther: '',
    // paymentMadeBy: '',
    // receivedBy: '',
    // billingName: '',
    // billingDate: '',
    // paymentSignUpDate: '',
    // contractDate: '',
    // paymentMode: '',
    // paymentModeOther: '',
    // paymentSignUpAmount: '',
    // contractAmount: '',
  });

  const residentialPackageList = [
    { id: 1, name: '1999' },
    { id: 2, name: '2249' },
    { id: 3, name: 'Other' },
  ];

  const designsPackageList = [
    { id: 1, name: 'Basic-35000' },
    { id: 2, name: 'Advance-65000' },
    { id: 3, name: 'Pro-1L+' },
  ];

  const commercialPackageList = [
    { id: 1, name: '999' },
    { id: 2, name: '1450' },
    { id: 3, name: 'Other' },
  ];

  const interiorsPackageList = [
    { id: 1, name: 'Design Consultation' },
    { id: 2, name: 'Package' },
    { id: 3, name: 'Other' },
  ];

  const paymentModeList = [
    { id: 1, name: 'Cash' },
    {
      id: 2,
      name: 'Cheque(Mention checque number in the additional information)',
    },
    { id: 3, name: 'NEFT' },
    { id: 4, name: 'IMPS' },
    { id: 5, name: 'UPI(Mention the url No.)' },
    { id: 6, name: 'DD' },
    { id: 7, name: 'RTGS' },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'toggleResidentialPackage') {
      setFormData({
        ...formData,
        [name]: e.target.checked,
        residentialPackage: null,
      });
    } else if (name === 'toggleCommercialPackage') {
      setFormData({
        ...formData,
        [name]: e.target.checked,
        commercialPackage: null,
      });
    } else if (name === 'toggleDesignsPackage') {
      setFormData({
        ...formData,
        [name]: e.target.checked,
        designsPackage: null,
      });
    } else if (name === 'toggleInteriorsPackage') {
      setFormData({
        ...formData,
        [name]: e.target.checked,
        interiorsPackage: null,
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // Perform validation
    setErrors({ ...errors, [name]: isEmpty(value) });
  };

  const handleUpdateLeadKycSubmit = (e) => {
    e.preventDefault();

    const updatedErrors = {
      residentialPackage: formData.toggleResidentialPackage && isEmpty(formData.residentialPackage),
      residentialPackageOther: formData.residentialPackage === 'Other' && isEmpty(formData.residentialPackageOther),
      designsPackage: formData.toggleDesignsPackage && isEmpty(formData.designsPackage),
      commercialPackage: formData.toggleCommercialPackage && isEmpty(formData.commercialPackage),
      commercialPackageOther: formData.commercialPackage === 'Other' && isEmpty(formData.commercialPackageOther),
      interiorsPackage: formData.toggleInteriorsPackage && isEmpty(formData.interiorsPackage),
      interiorsPackageOther: formData.interiorsPackage === 'Other' && isEmpty(formData.interiorsPackageOther),
      // paymentMadeBy: isEmpty(formData.paymentMadeBy),
      // receivedBy: isEmpty(formData.receivedBy),
      // billingName: isEmpty(formData.billingName),
      // billingDate: isEmpty(formData.billingDate),
      // paymentSignUpDate: isEmpty(formData.paymentSignUpDate),
      // contractDate: isEmpty(formData.contractDate),
      // paymentMode: isEmpty(formData.paymentMode),
      // paymentModeOther: isEmpty(formData.paymentModeOther),
      // paymentSignUpAmount: isEmpty(formData.paymentSignUpAmount),
      // contractAmount: isEmpty(formData.contractAmount)
    };
    setErrors(updatedErrors);
    debugger;
    if (!Object.values(updatedErrors).some((error) => error === true)) {
      const payload = { ...formData };
      isEmpty(formData.id) ? LeadStore.createPcpDetails(payload) : LeadStore.updatePcpDetails(payload);
    }
  };

  useEffect(() => {
    LeadStore.updatePcpDetailsStatus && handleEditPcpDetails();
  }, [LeadStore.updatePcpDetailsStatus]);

  const isContactNumberError = (value, name) => {
    if (isEmpty(value)) {
      return true;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  const isNumberError = (value, name) => {
    if (isEmpty(value)) {
      return false;
    } else if (!/^\d{0,10}$/.test(value)) {
      setErrorMessages({
        ...errors,
        [name]: 'Please enter valid 10 digit number',
      });
      return true;
    } else {
      return false;
    }
  };

  return (
    <Paper sx={{ px: '30px', py: '20px', pb: '40px' }}>
      <Box
        id="lead-kyc-details-form"
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={(event) => {
          event.preventDefault();
          handleUpdateLeadKycSubmit(event);
        }}
      >
        <Stack spacing={4}>
          <Stack spacing={3}>
            <Typography sx={{ fontWeight: '500', alignSelf: 'start' }} component="p">
              Signed-up Package Details
            </Typography>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <CustomFormControlLabel
                    control={
                      <Switch
                        checked={formData.toggleResidentialPackage}
                        size="medium"
                        onChange={handleChange}
                        name="toggleResidentialPackage"
                      />
                    }
                    label="Residential"
                  />
                  {formData.toggleResidentialPackage && (
                    <FormControl variant="standard" error={Boolean(errors.residentialPackage)}>
                      <InputLabel id="residentialPackageLabel" required shrink={true}>
                        Residential
                      </InputLabel>
                      <Select
                        labelId="residentialPackageLabel"
                        id="residentialPackage"
                        name="residentialPackage"
                        value={formData.residentialPackage ?? ''}
                        onChange={(e) => {
                          const {
                            target: { value },
                          } = e;
                          setFormData({
                            ...formData,
                            residentialPackage: value,
                            residentialPackageOther: value !== 'Other' ? null : formData.residentialPackageOther,
                          });
                          setErrors({
                            ...errors,
                            residentialPackage: isEmpty(value),
                          });
                        }}
                        label="Residential"
                        MenuProps={MenuProps}
                      >
                        {residentialPackageList.map((site) => (
                          <MenuItem key={site.id} value={site.name}>
                            <ListItemText primary={site.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {formData.residentialPackage === 'Other' && (
                    <TextField
                      label="Residential Other"
                      id="residentialPackageOther"
                      size="small"
                      variant="standard"
                      required={formData.residentialPackage === 'Other'}
                      name="residentialPackageOther"
                      multiline
                      rows={1}
                      value={formData.residentialPackageOther ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.residentialPackageOther)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                  <CustomFormControlLabel
                    control={
                      <Switch
                        checked={formData.toggleCommercialPackage}
                        size="medium"
                        onChange={handleChange}
                        name="toggleCommercialPackage"
                      />
                    }
                    label="Commercial"
                  />
                  {formData.toggleCommercialPackage && (
                    <FormControl variant="standard" error={Boolean(errors.commercialPackage)}>
                      <InputLabel id="commercialPackageLabel" required shrink={true}>
                        Commercial
                      </InputLabel>
                      <Select
                        labelId="commercialPackageLabel"
                        id="commercialPackage"
                        name="commercialPackage"
                        value={formData.commercialPackage ?? ''}
                        onChange={(e) => {
                          const {
                            target: { value },
                          } = e;
                          setFormData({
                            ...formData,
                            commercialPackage: value,
                            commercialPackageOther: value !== 'Other' ? null : formData.commercialPackageOther,
                          });
                          setErrors({
                            ...errors,
                            commercialPackage: isEmpty(value),
                          });
                        }}
                        label="Commercial"
                        MenuProps={MenuProps}
                      >
                        {commercialPackageList.map((site) => (
                          <MenuItem key={site.id} value={site.name}>
                            <ListItemText primary={site.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {formData.commercialPackage === 'Other' && (
                    <TextField
                      label="Commercial Other"
                      id="commercialPackageOther"
                      size="small"
                      variant="standard"
                      required={formData.commercialPackage === 'Other'}
                      name="commercialPackageOther"
                      multiline
                      rows={1}
                      value={formData.commercialPackageOther ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.commercialPackageOther)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <CustomFormControlLabel
                    control={
                      <Switch
                        checked={formData.toggleDesignsPackage}
                        size="medium"
                        onChange={handleChange}
                        name="toggleDesignsPackage"
                      />
                    }
                    label="Design"
                  />
                  {formData.toggleDesignsPackage && (
                    <FormControl variant="standard" error={Boolean(errors.designsPackage)}>
                      <InputLabel id="designsPackageLabel" required shrink={true}>
                        Design
                      </InputLabel>
                      <Select
                        labelId="designsPackageLabel"
                        id="designsPackage"
                        name="designsPackage"
                        value={formData.designsPackage ?? ''}
                        onChange={(e) => {
                          const {
                            target: { value },
                          } = e;
                          setFormData({ ...formData, designsPackage: value });
                          setErrors({
                            ...errors,
                            designsPackage: isEmpty(value),
                          });
                        }}
                        label="Design"
                        MenuProps={MenuProps}
                      >
                        {designsPackageList.map((site) => (
                          <MenuItem key={site.id} value={site.name}>
                            <ListItemText primary={site.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  <CustomFormControlLabel
                    control={
                      <Switch
                        checked={formData.toggleInteriorsPackage}
                        size="medium"
                        onChange={handleChange}
                        name="toggleInteriorsPackage"
                      />
                    }
                    label="Interiors"
                  />
                  {formData.toggleInteriorsPackage && (
                    <FormControl variant="standard" error={Boolean(errors.interiorsPackage)}>
                      <InputLabel id="interiorsPackageLabel" required shrink={true}>
                        Interiors
                      </InputLabel>
                      <Select
                        labelId="interiorsPackageLabel"
                        id="interiorsPackage"
                        name="interiorsPackage"
                        value={formData.interiorsPackage ?? ''}
                        onChange={(e) => {
                          const {
                            target: { value },
                          } = e;
                          setFormData({
                            ...formData,
                            interiorsPackage: value,
                            interiorsPackageOther: value !== 'Other' ? null : formData.interiorsPackageOther,
                          });
                          setErrors({
                            ...errors,
                            interiorsPackage: isEmpty(value),
                          });
                        }}
                        label="Interiors"
                        MenuProps={MenuProps}
                      >
                        {interiorsPackageList.map((site) => (
                          <MenuItem key={site.id} value={site.name}>
                            <ListItemText primary={site.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {formData.interiorsPackage === 'Other' && (
                    <TextField
                      label="Interiors Other"
                      id="interiorsPackageOther"
                      size="small"
                      variant="standard"
                      required={formData.interiorsPackage === 'Other'}
                      name="interiorsPackageOther"
                      multiline
                      rows={1}
                      value={formData.interiorsPackageOther ?? ''}
                      onChange={(e) => handleChange(e)}
                      error={Boolean(errors.interiorsPackageOther)}
                      sx={{ fontSize: '12px' }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack spacing={3}>
            <Typography sx={{ fontWeight: '500', alignSelf: 'start' }} component="p">
              Construction Details
            </Typography>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <TextField
                    label="Project Code"
                    id="projectCode"
                    size="small"
                    variant="standard"
                    name="projectCode"
                    value={formData.projectCode ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Tentative Project Value"
                    id="tentativeProjectValue"
                    size="small"
                    type="number"
                    variant="standard"
                    sx={{ fontSize: '12px' }}
                    name="tentativeProjectValue"
                    maxLength="300"
                    value={formData.tentativeProjectValue ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.tentativeProjectValue)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Final Project Value"
                    id="finalProjectValue"
                    size="small"
                    type="number"
                    variant="standard"
                    sx={{ fontSize: '12px' }}
                    name="finalProjectValue"
                    maxLength="300"
                    value={formData.finalProjectValue ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.finalProjectValue)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  {/* <TextField
                    label='Construction Value'
                    id="constructionValue"
                    size="small"
                    type="number"
                    variant="standard"
                    sx={{fontSize: '12px'}}
                    name="constructionValue"
                    maxLength="300"
                    value={formData.constructionValue ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.constructionValue)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  /> */}
                  <TextField
                    label="Elevation Value"
                    id="elevationValue"
                    size="small"
                    type="number"
                    variant="standard"
                    sx={{ fontSize: '12px' }}
                    name="elevationValue"
                    maxLength="300"
                    value={formData.elevationValue ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.elevationValue)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Additional Charged In Total"
                    id="additionalChargedInTotal"
                    size="small"
                    type="number"
                    variant="standard"
                    sx={{ fontSize: '12px' }}
                    name="additionalChargedInTotal"
                    maxLength="300"
                    value={formData.additionalChargedInTotal ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.additionalChargedInTotal)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Sign up Date"
                      value={formData.signUpDate ? new Date(formData.signUpDate) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: { name: 'signUpDate', value: newValue },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'signUpDate',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Soil Test Date"
                      value={formData.soilTestDate ? new Date(formData.soilTestDate) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: { name: 'soilTestDate', value: newValue },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'soilTestDate',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Total Station Date"
                      value={formData.totalStationDate ? new Date(formData.totalStationDate) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: 'totalStationDate',
                            value: newValue,
                          },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'totalStationDate',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Floor Plan Finalised Date"
                      value={formData.floorPlanFinalisedDate ? new Date(formData.floorPlanFinalisedDate) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: 'floorPlanFinalisedDate',
                            value: newValue,
                          },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'floorPlanFinalisedDate',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Elevation Finalised Date"
                      value={formData.elevationFinalisedDate ? new Date(formData.elevationFinalisedDate) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: 'elevationFinalisedDate',
                            value: newValue,
                          },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'elevationFinalisedDate',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Master File Finalised Date"
                      value={formData.masterFileFinalisedDate ? new Date(formData.masterFileFinalisedDate) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: 'masterFileFinalisedDate',
                            value: newValue,
                          },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'masterFileFinalisedDate',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Tentative Commencement Date"
                      value={formData.tentativeCommencementDate ? new Date(formData.tentativeCommencementDate) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: 'tentativeCommencementDate',
                            value: newValue,
                          },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'tentativeCommencementDate',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack spacing={3}>
            <Typography sx={{ fontWeight: '500', alignSelf: 'start' }} component="p">
              Team
            </Typography>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <TextField
                    label="Project Manager"
                    id="projectManager"
                    size="small"
                    variant="standard"
                    name="projectManager"
                    value={formData.projectManager ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Site Engineer"
                    id="siteEngineer"
                    size="small"
                    variant="standard"
                    name="siteEngineer"
                    value={formData.siteEngineer ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <TextField
                    label="Architect"
                    id="architect"
                    size="small"
                    variant="standard"
                    name="architect"
                    value={formData.architect ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Sales Partner"
                    id="salesPartner"
                    size="small"
                    variant="standard"
                    name="salesPartner"
                    value={formData.salesPartner ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack spacing={3}>
            <Typography sx={{ fontWeight: '500', alignSelf: 'start' }} component="p">
              Payment Details
            </Typography>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <TextField
                    label="Payment Made By"
                    id="paymentMadeBy"
                    size="small"
                    variant="standard"
                    name="paymentMadeBy"
                    value={formData.paymentMadeBy ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Received By"
                    id="receivedBy"
                    size="small"
                    variant="standard"
                    name="receivedBy"
                    value={formData.receivedBy ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Billing Name"
                    id="billingName"
                    size="small"
                    variant="standard"
                    name="billingName"
                    value={formData.billingName ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Billing Date"
                      value={formData.billingDate ? new Date(formData.billingDate) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: { name: 'billingDate', value: newValue },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'billingDate',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Sign-up Date"
                      value={formData.signUpDate ? new Date(formData.signUpDate) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: { name: 'signUpDate', value: newValue },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'signUpDate',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6} sx={{ pl: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={3}>
                  <FormControl variant="standard" error={Boolean(errors.paymentMode)}>
                    <InputLabel id="paymentModeLabel" shrink={true}>
                      Payment Mode
                    </InputLabel>
                    <Select
                      labelId="paymentModeLabel"
                      id="paymentMode"
                      name="paymentMode"
                      value={formData.paymentMode ?? ''}
                      onChange={(e) => {
                        const {
                          target: { value },
                        } = e;
                        setFormData({ ...formData, paymentMode: value });
                        setErrors({ ...errors, paymentMode: isEmpty(value) });
                      }}
                      label="Payment Mode"
                      MenuProps={MenuProps}
                    >
                      {paymentModeList.map((site) => (
                        <MenuItem key={site.id} value={site.name}>
                          <ListItemText primary={site.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Payment Mode Other Details"
                    id="paymentModeOther"
                    size="small"
                    variant="standard"
                    name="paymentModeOther"
                    value={formData.paymentModeOther ?? ''}
                    onChange={(e) => handleChange(e)}
                    sx={{ fontSize: '12px' }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Sign-up Amount"
                    id="paymentSignUpAmount"
                    size="small"
                    type="number"
                    variant="standard"
                    sx={{ fontSize: '12px' }}
                    name="paymentSignUpAmount"
                    maxLength="300"
                    value={formData.paymentSignUpAmount ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.paymentSignUpAmount)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <TextField
                    label="Contract Amount"
                    id="contractAmount"
                    size="small"
                    type="number"
                    variant="standard"
                    sx={{ fontSize: '12px' }}
                    name="contractAmount"
                    maxLength="300"
                    value={formData.contractAmount ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.contractAmount)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIN}>
                    <DatePicker
                      label="Contract Date"
                      value={formData.contractDate ? new Date(formData.contractDate) : null}
                      onChange={(newValue) => {
                        handleChange({
                          target: { name: 'contractDate', value: newValue },
                        });
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          variant: 'standard',
                          name: 'contractDate',
                          fontSize: '12px',
                          InputLabelProps: { shrink: true },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack spacing={3}>
            <Grid rowGap={2} container>
              <Grid item xs={12} sm={12} md={6} lg={6} sx={{ pr: isSmallScreen ? '0px' : '10px' }}>
                <Stack direction={'column'} spacing={2}>
                  <TextField
                    label="List of Additionals Promised"
                    id="listOfAdditionalsPromised"
                    size="small"
                    variant="standard"
                    multiline
                    maxRows={4}
                    sx={{ fontSize: '12px' }}
                    name="listOfAdditionalsPromised"
                    value={formData.listOfAdditionalsPromised ?? ''}
                    onChange={handleChange}
                    error={Boolean(errors.listOfAdditionalsPromised)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
          <Stack direction={'row'} sx={{ ml: 'auto' }} alignSelf={isSmallScreen ? '' : 'end'} spacing={2}>
            <Button
              variant="outlined"
              color="inherit"
              fullWidth
              size="small"
              onClick={() => handleEditPcpDetails(false)}
            >
              Cancel
            </Button>
            <Button variant="contained" type="submit" fullWidth color="primary" size="small">
              Update
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
});
